import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChefHat, ClipboardList, Plus, Salad, Trash2, Wheat } from "lucide-react";
import { ApiError, type NormativIngredientDto } from "@finora/api-client";
import type { ProductDto } from "@finora/types";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const MODULE_KEY = "normativ";
type TabKey = "namirnice" | "normativ" | "dodaci" | "izvestaji";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const UNITS = ["kg", "g", "l", "ml", "kom"];

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

function fmtQty(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("sr-RS", { maximumFractionDigits: 4 });
}

function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}

export function NormativPage() {
  const [tab, setTab] = useState<TabKey>("namirnice");
  const gate = useQuery({ queryKey: ["normativ-ingredients"], queryFn: () => api.listNormativIngredients(), retry: false });

  if (gate.isError) {
    if (gate.error instanceof ApiError && gate.error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={gate.error} moduleKey={MODULE_KEY} />;
    }
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(gate.error, "Učitavanje nije uspelo.")}</p>;
  }

  const tabs: Array<{ key: TabKey; label: string; icon: typeof Wheat }> = [
    { key: "namirnice", label: "Namirnice", icon: Wheat },
    { key: "normativ", label: "Normativ artikla", icon: ChefHat },
    { key: "dodaci", label: "Dodaci (gramaža)", icon: Salad },
    { key: "izvestaji", label: "Izveštaji", icon: ClipboardList },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Normativ</h1>
        <p className="text-sm text-slate-500">
          Recepti artikala i dodataka. Kad se izda račun, sastojci se automatski skidaju sa lagera (magacin prodajnog mesta).
          Ispravka recepta ne menja račune koji su već izdati.
        </p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="h-4 w-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "namirnice" && <IngredientsTab ingredients={gate.data ?? []} loading={gate.isLoading} />}
      {tab === "normativ" && <RecipeTab ingredients={gate.data ?? []} />}
      {tab === "dodaci" && <AddonsTab ingredients={gate.data ?? []} />}
      {tab === "izvestaji" && <ReportsTab ingredients={gate.data ?? []} />}
    </div>
  );
}

// --- Namirnice ---------------------------------------------------------------

function IngredientsTab({ ingredients, loading }: { ingredients: NormativIngredientDto[]; loading: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("kg");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createNormativIngredient({ name, unit }),
    onSuccess: () => {
      setName("");
      setErr(null);
      qc.invalidateQueries({ queryKey: ["normativ-ingredients"] });
    },
    onError: (e) => setErr(errMsg(e, "Dodavanje nije uspelo.")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.setNormativIngredientFlag(id, false),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["normativ-ingredients"] });
    },
    onError: (e) => setErr(errMsg(e, "Uklanjanje nije uspelo.")),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Nova namirnica</h2>
        <p className="mb-3 text-xs text-slate-500">
          Namirnica je sastojak (brašno, šunka, sir). Ne prodaje se i ne prikazuje se na kasi, ali ima lager i ulazi u normativ.
          Količinu na lager unosite kroz Magacinsko poslovanje → Prijem robe.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto]">
          <input className={inputCls} placeholder="Naziv (npr. Šunka)" value={name} onChange={(e) => setName(e.target.value)} />
          <select className={inputCls} value={unit} onChange={(e) => setUnit(e.target.value)}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> Dodaj
          </Button>
        </div>
        {err && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      </div>

      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : ingredients.length === 0 ? (
        <EmptyState icon={Wheat} title="Nema namirnica" description="Dodajte prvu namirnicu iznad." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-app-bg text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-2">Namirnica</th>
                <th className="px-4 py-2">Šifra</th>
                <th className="px-4 py-2">Jedinica</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {ingredients.map((i) => (
                <tr key={i.id} className="border-t border-border">
                  <td className="px-4 py-2 font-medium text-slate-800">{i.name}</td>
                  <td className="px-4 py-2 text-slate-500">{i.sku}</td>
                  <td className="px-4 py-2 text-slate-700">{i.unit}</td>
                  <td className="px-2">
                    <button
                      title="Ukloni oznaku namirnice"
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      onClick={() => {
                        if (confirm(`Ukloniti oznaku namirnice sa „${i.name}“?`)) remove.mutate(i.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Normativ artikla ---------------------------------------------------------

type RecipeRow = { key: string; ingredientId: string; quantity: string; wastePercent: string; variationId: string };

function RecipeTab({ ingredients }: { ingredients: NormativIngredientDto[] }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [rows, setRows] = useState<RecipeRow[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const products = useQuery({ queryKey: ["normativ-products", search], queryFn: () => api.listProducts({ search: search || undefined }) });
  const withRecipe = useQuery({ queryKey: ["normativ-recipes"], queryFn: () => api.listNormativRecipes() });
  const productFromList: ProductDto | undefined = products.data?.find((p) => p.id === productId);
  const productQ = useQuery({
    queryKey: ["normativ-product", productId],
    queryFn: () => api.getProduct(productId as string),
    enabled: !!productId && !productFromList,
  });
  const product = (productFromList ?? productQ.data) as ProductDto | undefined;

  const recipe = useQuery({
    queryKey: ["normativ-recipe", productId],
    queryFn: () => api.getNormativRecipe(productId as string),
    enabled: !!productId,
  });
  useEffect(() => {
    if (!recipe.data) return;
    setRows(
      recipe.data.map((l) => ({
        key: l.id,
        ingredientId: l.ingredientId,
        quantity: String(Number(l.quantity)),
        wastePercent: String(Number(l.wastePercent)),
        variationId: l.variationId ?? "",
      })),
    );
    setMsg(null);
  }, [recipe.data]);

  const save = useMutation({
    mutationFn: () =>
      api.setNormativRecipe(
        productId as string,
        rows
          .filter((r) => r.ingredientId)
          .map((r) => ({
            ingredientId: r.ingredientId,
            quantity: r.quantity.replace(",", "."),
            wastePercent: (r.wastePercent || "0").replace(",", "."),
            variationId: r.variationId || null,
          })),
      ),
    onSuccess: () => {
      setMsg({ ok: true, text: "Normativ je sačuvan." });
      qc.invalidateQueries({ queryKey: ["normativ-recipe", productId] });
      qc.invalidateQueries({ queryKey: ["normativ-recipes"] });
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });

  const unitOf = (id: string) => ingredients.find((i) => i.id === id)?.unit ?? "";
  const update = (key: string, patch: Partial<RecipeRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-800">Artikli sa normativom</h2>
        <div className="relative w-full sm:w-80">
          <input
            className={inputCls}
            placeholder="Pretraži artikal i otvori normativ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search.trim() && (
            <div className="absolute right-0 z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-border bg-surface shadow-lg">
              {products.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : (products.data ?? []).length === 0 ? (
                <p className="p-3 text-sm text-slate-400">Nema artikala.</p>
              ) : (
                (products.data ?? []).slice(0, 30).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setProductId(p.id);
                      setSearch("");
                    }}
                    className="block w-full border-b border-border px-3 py-2 text-left text-sm text-slate-700 last:border-0 hover:bg-app-bg"
                  >
                    {p.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {withRecipe.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (withRecipe.data ?? []).length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Još nijedan artikal nema normativ. Pretražite artikal gore desno da ga dodate.</p>
        ) : (
          <div className="flex flex-wrap gap-2 p-3">
            {(withRecipe.data ?? []).map((r) => (
              <button
                key={r.productId}
                onClick={() => setProductId(r.productId)}
                className={`rounded-lg border px-3 py-1.5 text-sm ${
                  r.productId === productId ? "border-brand bg-brand/10 font-medium text-brand" : "border-border text-slate-700 hover:bg-app-bg"
                }`}
              >
                {r.name} <span className="text-xs text-slate-400">· {r.ingredients}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        {!product ? (
          <p className="text-sm text-slate-400">Izaberite artikal iz liste iznad ili ga pretražite gore desno.</p>
        ) : (
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">{product.name}</h2>
            <p className="text-xs text-slate-500">
              Količina je u jedinici namirnice (npr. za kg upišite 0,2 za 200 g). Kalo je rastur u procentima. Red vezan za varijaciju
              važi samo za nju, a bez varijacije važi za sve.
            </p>
            {recipe.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <>
                {rows.length === 0 && <p className="text-sm text-slate-400">Normativ je prazan, ovaj artikal ništa ne skida.</p>}
                {rows.map((r) => (
                  <div key={r.key} className="grid grid-cols-2 items-center gap-2 sm:grid-cols-[1.4fr_90px_70px_1fr_36px]">
                    <select className={inputCls} value={r.ingredientId} onChange={(e) => update(r.key, { ingredientId: e.target.value })}>
                      <option value="">— namirnica —</option>
                      {ingredients.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1">
                      <input
                        className={inputCls}
                        inputMode="decimal"
                        placeholder="Količina"
                        value={r.quantity}
                        onChange={(e) => update(r.key, { quantity: e.target.value })}
                      />
                      <span className="w-6 text-xs text-slate-500">{unitOf(r.ingredientId)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        className={inputCls}
                        inputMode="decimal"
                        placeholder="Kalo"
                        value={r.wastePercent}
                        onChange={(e) => update(r.key, { wastePercent: e.target.value })}
                      />
                      <span className="text-xs text-slate-500">%</span>
                    </div>
                    <select className={inputCls} value={r.variationId} onChange={(e) => update(r.key, { variationId: e.target.value })}>
                      <option value="">Sve varijacije</option>
                      {(product.variations ?? []).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Ukloni red"
                      onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setRows((rs) => [...rs, { key: `new-${Date.now()}-${rs.length}`, ingredientId: "", quantity: "", wastePercent: "0", variationId: "" }])
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" aria-hidden /> Dodaj sastojak
                  </Button>
                  <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                    {save.isPending ? "Čuvam..." : "Sačuvaj normativ"}
                  </Button>
                  {msg && <span className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-700"}`}>{msg.text}</span>}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Dodaci -----------------------------------------------------------------

function AddonsTab({ ingredients }: { ingredients: NormativIngredientDto[] }) {
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ["normativ-products-addons"], queryFn: () => api.listProducts() });
  const withAddons = useMemo(
    () => (products.data ?? []).filter((p) => (p.attributeGroups ?? []).some((g) => g.options.length > 0)),
    [products.data],
  );
  const [edits, setEdits] = useState<Record<string, { ingredientId: string; quantity: string }>>({});
  const [msg, setMsg] = useState<{ id: string; ok: boolean; text: string } | null>(null);

  const save = useMutation({
    mutationFn: (v: { optionId: string; ingredientId: string; quantity: string }) =>
      api.setNormativOption(v.optionId, {
        ingredientProductId: v.ingredientId || null,
        ingredientQuantity: v.ingredientId ? v.quantity.replace(",", ".") : null,
      }),
    onSuccess: (_d, v) => {
      setMsg({ id: v.optionId, ok: true, text: "Sačuvano." });
      qc.invalidateQueries({ queryKey: ["normativ-products-addons"] });
    },
    onError: (e, v) => setMsg({ id: v.optionId, ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });

  if (products.isLoading) return <Skeleton className="h-24 w-full" />;
  if (withAddons.length === 0) {
    return <EmptyState icon={Salad} title="Nema artikala sa dodacima" description="Dodatke dodajete na artiklu (Artikli i usluge → artikal → Dodaci), pa im ovde zadajte gramažu." />;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Za svaki dodatak izaberite namirnicu i količinu koju troši. Dodatak bez namirnice ništa ne skida (npr. „hrskava kora“).
      </p>
      {withAddons.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <h2 className="mb-2 text-sm font-semibold text-slate-900">{p.name}</h2>
          {(p.attributeGroups ?? []).map((g) => (
            <div key={g.id} className="mb-3 last:mb-0">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">{g.name}</div>
              {g.options.map((o) => {
                const cur = edits[o.id] ?? { ingredientId: o.ingredientProductId ?? "", quantity: o.ingredientQuantity ? String(Number(o.ingredientQuantity)) : "" };
                const unit = ingredients.find((i) => i.id === cur.ingredientId)?.unit ?? "";
                const dirty = cur.ingredientId !== (o.ingredientProductId ?? "") || cur.quantity !== (o.ingredientQuantity ? String(Number(o.ingredientQuantity)) : "");
                return (
                  <div key={o.id} className="mb-2 grid grid-cols-2 items-center gap-2 sm:grid-cols-[1fr_1.2fr_110px_auto]">
                    <span className="text-sm text-slate-800">{o.name}</span>
                    <select
                      className={inputCls}
                      value={cur.ingredientId}
                      onChange={(e) => setEdits((s) => ({ ...s, [o.id]: { ...cur, ingredientId: e.target.value } }))}
                    >
                      <option value="">— ništa ne skida —</option>
                      {ingredients.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1">
                      <input
                        className={inputCls}
                        inputMode="decimal"
                        placeholder="Količina"
                        disabled={!cur.ingredientId}
                        value={cur.quantity}
                        onChange={(e) => setEdits((s) => ({ ...s, [o.id]: { ...cur, quantity: e.target.value } }))}
                      />
                      <span className="w-6 text-xs text-slate-500">{unit}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!dirty || save.isPending}
                        onClick={() => save.mutate({ optionId: o.id, ingredientId: cur.ingredientId, quantity: cur.quantity })}
                      >
                        Sačuvaj
                      </Button>
                      {msg?.id === o.id && <span className={`text-xs ${msg.ok ? "text-emerald-700" : "text-red-700"}`}>{msg.text}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// --- Izveštaji ----------------------------------------------------------------

function ReportsTab({ ingredients }: { ingredients: NormativIngredientDto[] }) {
  const today = new Date();
  const [from, setFrom] = useState(isoDay(new Date(today.getTime() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(today));
  const [negOnly, setNegOnly] = useState(false);

  const consumption = useQuery({ queryKey: ["normativ-consumption", from, to], queryFn: () => api.getNormativConsumption({ from, to }) });
  const addons = useQuery({ queryKey: ["normativ-addon-sales", from, to], queryFn: () => api.getNormativAddonSales({ from, to }) });
  const moves = useQuery({ queryKey: ["normativ-movements", from, to, negOnly], queryFn: () => api.getNormativMovements({ from, to, negativeOnly: negOnly }) });
  const nameOf = (id: string) => ingredients.find((i) => i.id === id)?.name ?? "—";
  const unitOf = (id: string) => ingredients.find((i) => i.id === id)?.unit ?? "";

  const totals = useMemo(() => {
    const m = new Map<string, { name: string; unit: string; qty: number }>();
    for (const r of consumption.data ?? []) {
      const cur = m.get(r.productId) ?? { name: r.name, unit: r.unit, qty: 0 };
      cur.qty += Number(r.quantity);
      m.set(r.productId, cur);
    }
    return [...m.values()].sort((a, b) => b.qty - a.qty);
  }, [consumption.data]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">
          Od
          <input type="date" className={`${inputCls} mt-1 w-40`} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="text-xs text-slate-500">
          Do
          <input type="date" className={`${inputCls} mt-1 w-40`} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Utrošak namirnica</h2>
        {consumption.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : totals.length === 0 ? (
          <p className="text-sm text-slate-400">Nema utroška u izabranom periodu.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <table className="w-full overflow-hidden rounded-xl border border-border bg-surface text-sm shadow-subtle">
              <thead className="bg-app-bg text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2">Ukupno za period</th>
                  <th className="px-4 py-2 text-right">Količina</th>
                </tr>
              </thead>
              <tbody>
                {totals.map((t) => (
                  <tr key={t.name} className="border-t border-border">
                    <td className="px-4 py-2">{t.name}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      {fmtQty(t.qty)} {t.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="w-full overflow-hidden rounded-xl border border-border bg-surface text-sm shadow-subtle">
              <thead className="bg-app-bg text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2">Dan</th>
                  <th className="px-4 py-2">Namirnica</th>
                  <th className="px-4 py-2 text-right">Količina</th>
                </tr>
              </thead>
              <tbody>
                {(consumption.data ?? []).map((r) => (
                  <tr key={`${r.day}-${r.productId}`} className="border-t border-border">
                    <td className="px-4 py-2 text-slate-500">{r.day.split("-").reverse().join(".")}.</td>
                    <td className="px-4 py-2">{r.name}</td>
                    <td className="px-4 py-2 text-right">
                      {fmtQty(r.quantity)} {r.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-800">Prodaja dodataka</h2>
        {addons.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (addons.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">Nema prodatih dodataka u izabranom periodu.</p>
        ) : (
          <table className="w-full overflow-hidden rounded-xl border border-border bg-surface text-sm shadow-subtle">
            <thead className="bg-app-bg text-left text-xs text-slate-500">
              <tr>
                <th className="px-4 py-2">Dodatak</th>
                <th className="px-4 py-2 text-right">Prodato (kom)</th>
                <th className="px-4 py-2">Namirnica</th>
                <th className="px-4 py-2 text-right">Ukupno utrošeno</th>
              </tr>
            </thead>
            <tbody>
              {(addons.data ?? []).map((a) => (
                <tr key={`${a.name}-${a.ingredientName}`} className="border-t border-border">
                  <td className="px-4 py-2">{a.name}</td>
                  <td className="px-4 py-2 text-right font-medium">{a.times}</td>
                  <td className="px-4 py-2 text-slate-500">{a.ingredientName ?? "—"}</td>
                  <td className="px-4 py-2 text-right">{a.totalQuantity ? `${fmtQty(a.totalQuantity)} ${a.unit ?? ""}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Dnevnik kretanja lagera</h2>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={negOnly} onChange={(e) => setNegOnly(e.target.checked)} />
            Samo kad je lager otišao u minus
          </label>
        </div>
        {moves.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (moves.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">Nema kretanja.</p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto rounded-xl border border-border bg-surface shadow-subtle">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-app-bg text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2">Vreme</th>
                  <th className="px-4 py-2">Namirnica</th>
                  <th className="px-4 py-2 text-right">Promena</th>
                  <th className="px-4 py-2 text-right">Stanje posle</th>
                  <th className="px-4 py-2">Razlog</th>
                </tr>
              </thead>
              <tbody>
                {(moves.data ?? []).map((m) => (
                  <tr key={m.id} className="border-t border-border">
                    <td className="px-4 py-2 text-slate-500">{new Date(m.createdAt).toLocaleString("sr-RS")}</td>
                    <td className="px-4 py-2">{nameOf(m.productId)}</td>
                    <td className={`px-4 py-2 text-right ${Number(m.quantity) < 0 ? "text-slate-800" : "text-emerald-700"}`}>
                      {fmtQty(m.quantity)} {unitOf(m.productId)}
                    </td>
                    <td className={`px-4 py-2 text-right ${m.negative ? "font-semibold text-red-600" : ""}`}>
                      {fmtQty(m.resultingQuantity)}
                      {m.negative ? " (minus)" : ""}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {m.reason === "NORMATIV_REFUND" ? "Storno" : "Prodaja"}
                      {m.source === "ADDON" ? " · dodatak" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
